import React from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import countriesData from './countries.json';

// Fix for default icon issue with webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Create custom colored icons for different order statuses
const createColoredIcon = (colors) => {
  // If single color, create a solid pin
  if (colors.length === 1) {
    return L.divIcon({
      className: 'custom-marker',
      html: `<div style="
        background-color: ${colors[0]};
        width: 25px;
        height: 25px;
        border-radius: 50% 50% 50% 0;
        transform: rotate(-45deg);
        border: 2px solid white;
        box-shadow: 0 2px 5px rgba(0,0,0,0.3);
      "></div>`,
      iconSize: [25, 25],
      iconAnchor: [12, 24],
      popupAnchor: [0, -24]
    });
  }
  
  // For multiple colors, create a gradient or striped pattern
  const gradientStops = colors.map((color, idx) => {
    const percent = (idx / colors.length) * 100;
    const nextPercent = ((idx + 1) / colors.length) * 100;
    return `${color} ${percent}%, ${color} ${nextPercent}%`;
  }).join(', ');
  
  return L.divIcon({
    className: 'custom-marker',
    html: `<div style="
      background: linear-gradient(135deg, ${gradientStops});
      width: 25px;
      height: 25px;
      border-radius: 50% 50% 50% 0;
      transform: rotate(-45deg);
      border: 2px solid white;
      box-shadow: 0 2px 5px rgba(0,0,0,0.3);
    "></div>`,
    iconSize: [25, 25],
    iconAnchor: [12, 24],
    popupAnchor: [0, -24]
  });
};

const statusColors = {
  committed: '#1976d2',      // Blue
  issued: '#7b1fa2',          // Purple
  partially_returned: '#f57c00' // Orange
};

const OrderLocationsMap = ({ locations }) => {
  if (!locations || locations.length === 0) {
    return <div style={{ height: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f9f9f9' }}>No locations to display</div>;
  }

  // Get coordinates from countries.json
  const getCoordinates = (locationName) => {
    const country = countriesData.find(
      c => c.name.toLowerCase() === locationName.toLowerCase()
    );
    return country ? country.coordinates : [51.505, -0.09]; // Default to London if not found
  };

  const position = getCoordinates(locations[0].name);

  // Get the colors for each location based on status distribution
  const getLocationIcon = (orders) => {
    const statusCounts = orders.reduce((acc, order) => {
      acc[order.orderStatus] = (acc[order.orderStatus] || 0) + 1;
      return acc;
    }, {});
    
    // Get unique statuses and their colors
    const uniqueStatuses = Object.keys(statusCounts);
    const colors = uniqueStatuses
      .map(status => statusColors[status])
      .filter(color => color); // Remove any undefined colors
    
    // If no valid colors, use default
    if (colors.length === 0) {
      return createColoredIcon(['#666666']);
    }
    
    return createColoredIcon(colors);
  };

  return (
    <MapContainer 
      key={locations.map(l => l.name).join('-')} // Force re-render when locations change
      center={position} 
      zoom={4} 
      style={{ height: '400px', width: '100%' }}
      attributionControl={false}
    >
      <TileLayer
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      {locations.map((loc, idx) => (
        <Marker key={idx} position={getCoordinates(loc.name)} icon={getLocationIcon(loc.orders)}>
          <Popup>
            <strong>{loc.name}</strong>
            <br />
            Total Orders: {loc.orders.length}
            <ul style={{ marginTop: '5px', paddingLeft: '20px' }}>
              {loc.orders.slice(0, 5).map((order, oIdx) => (
                <li key={oIdx}>
                  <span style={{ 
                    display: 'inline-block',
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: statusColors[order.orderStatus] || '#666',
                    marginRight: '5px'
                  }}></span>
                  {order.orderName} (<em>{order.userName}</em>)
                </li>
              ))}
              {loc.orders.length > 5 && <li>...and {loc.orders.length - 5} more</li>}
            </ul>
          </Popup>
        </Marker>
      ))}
    </MapContainer>
  );
};

export default OrderLocationsMap;
